<div class="scroll-container">
    <div class="card mb-1 mt-3 py-2">
        <div class="card-header">
            <h4 class="card-title" [translate]="'LOCATION_TRACKER_TITLE'"></h4>
        </div>
        <div class="card-body">
            <!-- User Settings Section -->
            <section>
                <h5 [translate]="'LOCATION_TRACKER_USER_SETTINGS_TITLE'"></h5>

                <!--
                    Gated on `isDraftReady()` — the draft having been seeded from the user store —
                    and deliberately not on the user store merely being loaded, which a cache
                    rehydration also satisfies. The resolver this page used to sit behind blocked
                    navigation until the config arrived, so nothing could ever type into a form that
                    was about to be replaced; gating on the weaker condition would reopen exactly
                    that hole.
                -->
                @if (isDraftReady()) {
                    <div class="row mb-3">
                        <div class="row col-lg-6 align-items-center my-3">
                            <div class="col-lg-3 pb-2">
                                <span
                                    class="col-form-label text-start"
                                    [translate]="'LOCATION_TRACKER_STATE_LABEL'"
                                ></span>
                            </div>
                            <div class="col-lg-9">
                                <pace-text-field
                                    [formField]="locationTrackerForm.state"
                                    [ariaLabel]="'LOCATION_TRACKER_STATE_LABEL' | translate"
                                ></pace-text-field>
                            </div>
                        </div>

                        <div class="row col-lg-6 align-items-center my-3">
                            <div class="col-lg-3 pb-2">
                                <span
                                    class="col-form-label text-start"
                                    [translate]="'LOCATION_TRACKER_BUSINESS_LINE_LABEL'"
                                ></span>
                            </div>
                            <div class="col-lg-9">
                                <pace-text-field
                                    [formField]="locationTrackerForm.businessLine"
                                    [ariaLabel]="'LOCATION_TRACKER_BUSINESS_LINE_LABEL' | translate"
                                ></pace-text-field>
                            </div>
                        </div>
                    </div>

                    <div class="row mb-3">
                        <div class="row col-lg-6 align-items-center mb-3">
                            <div class="pb-2">
                                <span
                                    class="col-form-label text-start"
                                    [translate]="'LOCATION_TRACKER_SHARE_GEOLOCATION_LABEL'"
                                ></span>
                                <p class="mb-0">
                                    <i [translate]="'LOCATION_TRACKER_SHARE_GEOLOCATION_INFO'"> </i>
                                </p>
                            </div>
                            <div>
                                <pace-checkbox-field
                                    [formField]="locationTrackerForm.shareLocation"
                                    [ariaLabel]="'LOCATION_TRACKER_SHARE_GEOLOCATION_LABEL' | translate"
                                ></pace-checkbox-field>
                            </div>
                        </div>

                        <div class="row col-lg-6 align-items-center mb-3">
                            <div class="pb-2">
                                <span
                                    class="col-form-label text-start"
                                    [translate]="'LOCATION_TRACKER_ENABLE_PRIVACY_PERIMETER_LABEL'"
                                ></span>
                                <p class="mb-0">
                                    <i [translate]="'LOCATION_TRACKER_ENABLE_PRIVACY_PERIMETER_INFO'"> </i>
                                </p>
                            </div>
                            <div>
                                <pace-privacy-perimeter-picker
                                    [isSet]="isPrivacyPerimeterSet()"
                                    [isPending]="isPrivacyPerimeterRequestPending()"
                                    [editable]="isLocationTrackerEnabled()"
                                    [selectLabel]="'LOCATION_TRACKER_SELECT_LOCATION_LABEL' | translate"
                                    [pendingLabel]="'LOCATION_TRACKER_LOADING_LOCATION' | translate"
                                    [setLabel]="'LOCATION_TRACKER_LOCATION_SET' | translate"
                                    [errorText]="perimeterErrorText()"
                                    [clearAriaLabel]="'LOCATION_TRACKER_CLEAR_LOCATION_LABEL' | translate"
                                    (perimeterRequested)="selectLocation()"
                                    (perimeterCleared)="clearLocation()"
                                ></pace-privacy-perimeter-picker>
                            </div>
                        </div>
                    </div>

                    @if (!isLocationTrackerEnabled()) {
                        <p class="mb-0 text-danger">
                            <i [translate]="'LOCATION_TRACKER_DISABLED_INFO'"> </i>
                        </p>
                    }
                } @else {
                    <p class="mb-0" [translate]="'LOCATION_TRACKER_LOADING'"></p>
                }
            </section>

            <hr class="separator" />

            <!-- Server/Admin Configuration Section -->
            <section>
                <h5 [translate]="'LOCATION_TRACKER_SERVER_CONFIGURATION_TITLE'" class="mb-3"></h5>

                <div class="row align-items-center my-2">
                    <span
                        class="col-lg-3 col-form-label text-start"
                        [translate]="'LOCATION_TRACKER_AVERAGE_PRIVACY_ZONE_RADIUS_LABEL'"
                    ></span>
                    <div class="col-lg-9">
                        @if (locationTrackerConfig()?.privacyZoneRadiusMeters; as value) {
                            <span>{{ value }} m</span>
                        }
                    </div>
                </div>

                <div class="row align-items-center">
                    <span
                        class="col-lg-3 col-form-label text-start"
                        [translate]="'LOCATION_TRACKER_PRECISION_OF_LOCATION_UPDATES_LABEL'"
                    ></span>
                    <div class="col-lg-9">
                        @if (locationTrackerConfig()?.precisionMeters; as value) {
                            <span>{{ value }} m</span>
                        }
                    </div>
                </div>
            </section>
        </div>
        <div class="card-footer text-end d-lg-block d-none">
            <pace-save-action
                layout="inline"
                [label]="'LOCATION_TRACKER_SAVE_BUTTON' | translate"
                [isSaving]="isSaving()"
                [disabled]="!isDraftReady()"
                [message]="saveMessage()"
                (saveRequested)="save()"
            ></pace-save-action>
        </div>
    </div>
</div>

<!-- Mobile Footerbar: to show save button and messages without needing to scroll to the bottom of the page -->
<div class="d-block d-lg-none navbar bg-light fixed-bottom safe-area-navbar-bottom px-3">
    <pace-save-action
        layout="stacked"
        [label]="'LOCATION_TRACKER_SAVE_BUTTON' | translate"
        [isSaving]="isSaving()"
        [disabled]="!isDraftReady()"
        [message]="saveMessage()"
        (saveRequested)="save()"
    ></pace-save-action>
</div>
